// Changing records from a report, as list views do: a detail cell edits the
// record it shows, through the same PATCH /api/objects/:object/:id route, the
// same writable-field rules (GET /api/reports/schema's `writable`, from
// writes.mjs) and the same editors (writes.ts editorFor). Free of React and of
// the network, so it can be unit tested.
//
// What a report field edits:
//   stage                    the report's own record (row id)
//   account.industry         the account the row looks up to (row account.id)
//   account.name             the lookup itself: the row's account_id, picked from accounts
//   project.account.name     the project's account_id (row project.id)
// Summaries, groups and formulas are not detail cells, so they never edit.
import type { ReportField, ReportRow, ReportSchemaObject, ReportType } from '@olytic/foundation/reports';

import type { ObjectKey } from '../navigation';
import { editorFor, type LoadOptions, toWire, type WritableField } from '../objects/writes';

export type WritableByObject = Record<string, WritableField[]>;

export interface EditTarget {
  /** The object of the record the edit saves to. */
  object: ObjectKey;
  /** The row key holding that record's id. */
  idKey: string;
  /** The writable field the edit sets. */
  writable: WritableField;
  /** For a lookup: the row key holding the id it points at now. */
  valueKey?: string;
}

/** The lookup prefix of a schema field, as Foundation's reportTypesFromSchema names it: `account_id` → `account`. */
const prefixOf = (field: ReportSchemaObject['fields'][number]) => field.lookup?.key ?? field.key.replace(/_id$/, '');

/** Every field of a report type that the person may change in place, and what it changes. */
export function editTargets(type: ReportType, schema: ReportSchemaObject[], writable: WritableByObject): Map<string, EditTarget> {
  const byKey = new Map(schema.map((o) => [o.key, o]));
  const base = byKey.get(type.key);
  const targets = new Map<string, EditTarget>();
  if (!base) return targets;
  const own = (object: string, key: string) => (writable[object] ?? []).find((w) => w.key === key && w.type !== 'lookup');
  for (const field of type.fields) {
    const segments = field.key.split('.');
    const name = segments.pop()!;
    // Walk the lookups to the object the field belongs to, keeping each step.
    const steps: { object: ReportSchemaObject; via?: ReportSchemaObject['fields'][number] }[] = [{ object: base }];
    let ok = true;
    for (const segment of segments) {
      const from = steps[steps.length - 1]!.object;
      const via = from.fields.find((f) => f.lookup && prefixOf(f) === segment);
      const to = via?.lookup ? byKey.get(via.lookup.object) : undefined;
      if (!via || !to) { ok = false; break; }
      steps.push({ object: to, via });
    }
    if (!ok) continue;
    const at = steps[steps.length - 1]!.object;
    const path = segments.join('.');
    if (segments.length > 0 && name === (at.nameField ?? 'name')) {
      // A lookup's name field stands for the lookup: edit the foreign key on the record that holds it.
      const owner = steps[steps.length - 2]!.object;
      const via = steps[steps.length - 1]!.via!;
      const w = (writable[owner.key] ?? []).find((x) => x.key === via.key && x.type === 'lookup');
      const ownerPath = segments.slice(0, -1).join('.');
      if (w) targets.set(field.key, { object: owner.key as ObjectKey, idKey: ownerPath ? `${ownerPath}.id` : 'id', writable: w, valueKey: `${path}.id` });
      continue;
    }
    const w = own(at.key, name);
    if (w) targets.set(field.key, { object: at.key as ObjectKey, idKey: path ? `${path}.id` : 'id', writable: w });
  }
  return targets;
}

/** The report type with an editor on each field it may change: the list views' editor, reading the report row. */
export function withEditors(type: ReportType, targets: Map<string, EditTarget>, loadOptions: LoadOptions): ReportType {
  return {
    ...type,
    fields: type.fields.map((field): ReportField => {
      const target = targets.get(field.key);
      if (!target) return field;
      const editor = editorFor(target.writable, loadOptions);
      const read = target.valueKey ?? field.key;
      const date = target.writable.type === 'date';
      return {
        ...field,
        editor: {
          ...editor,
          getValue: (row: ReportRow) => {
            const value = row[read];
            if (value === null || value === undefined) return '';
            return date ? String(value).slice(0, 10) : String(value);
          },
        } as ReportField['editor'],
      };
    }),
  };
}

/** The record an edit saves to, and the PATCH body: keyed by the writable field, typed for the wire. */
export function cellEdit(target: EditTarget, row: ReportRow, value: unknown): { id: string; body: Record<string, unknown> } {
  const id = row[target.idKey];
  if (typeof id !== 'string' || id === '') throw new Error('This record could not be found.');
  const text = value === null || value === undefined ? null : String(value);
  return { id, body: { [target.writable.key]: toWire(target.writable, text) } };
}
